<template>
    <TransitionRoot as="template" class="transition-root" :show="open">
      <Dialog as="div" class="relative z-10" @close="open = false">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          />
        </TransitionChild>
  
        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
              >
                <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div class="sm:flex sm:items-start">
                    <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <DialogTitle
                        as="h3"
                        class="text-base font-semibold leading-6 text-gray-900"
                        >Select the Area
                      </DialogTitle>
                      <div class="mt-2">
                        <div>
                          <div class="image-container">
                            <vue-cropper
                              ref="cropper"
                              :src="imageUrl"
                              :rotate-to="rotateTo"
                            ></vue-cropper>
                            <!-- <button @click="rotateLeft">Rotate Left</button> -->
                            <!-- <button @click="rotateRight">Rotate Right</button> -->
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="w-full flex justify-center">
                  <div class="cropped-image-container">
                    <div v-if="croppedImageData" class="cropped-image">
                      <img
                        :src="croppedImageData"
                        :style="{ transform: `rotate(${rotation}deg)` }"
                        alt="Cropped Image"
                      />
                    </div>
                  </div>
                </div>
                <div
                  class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
                >
                  <button
                    @click="cropImage"
                    type="button"
                    class="inline-flex w-full justify-center rounded-md bg-green-600 px-5 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto"
                  >
                    Crop Image
                  </button>
                  <button
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    @click="close"
                    ref="cancelButtonRef"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </template>
  
  <script setup>
  import { ref, onMounted, onBeforeUnmount } from "vue";
  import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
  } from "@headlessui/vue";
  
  import { defineEmits } from "vue";
  
  import VueCropper from "vue-cropperjs";
  import 'cropperjs/dist/cropper.css';
  
  const emit = defineEmits();
  
  const props = defineProps({
    src: String, // Define the 'src' prop to receive the image URL
    order: Number,
  });
  
  // import { ExclamationTriangleIcon } from "@heroicons/vue/outline"; // Updated import path
  const imageUrl = ref(props.src);
  const cropper = ref(null);
  const croppedImageData = ref(null);
  const rotation = ref(0);
  
  const rotateLeft = () => {
    rotation.value -= 90;
  };
  
  const rotateRight = () => {
    rotation.value += 90;
  };
  
  const rotateTo = ref(0); // Initial rotation
  const cropImageFile = ref("");
  const cropImage = async () => {
    const canvas = await cropper.value.getCroppedCanvas();
    if (canvas) {
      // Convert the canvas to a Blob (File-like object)
      const blob = await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, "image/png");
      });
  
      if (blob) {
        // Create a File object from the Blob
        const fileName = "cropped-image.png"; // You can customize the file name
        const file = await new File([blob], fileName, {
          type: "image/png",
        });
        cropImageFile.value = file;
  
        // Now, `croppedImageFile` contains the cropped image as a File object
        // You can do further processing or save it as needed
  
        // Optionally, you can set `croppedImageData` to the data URL for display
        croppedImageData.value = URL.createObjectURL(blob);
  
        // Save the current rotation to `rotateTo` for reuse
        rotateTo.value = rotation.value;
      }
    }
    edit();
  };
  
  const open = ref(true);
  
  const edit = () => {
    emit("updateCropperImg", cropImageFile.value, props.order);
    emit("closeCropperModle", false);
  };
  const close = () => {
    open.value = false;
    emit("closeCropperModle", false);
  };
  
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      // Call the `close` function when "Esc" is pressed
      close();
    }
  });
  const handleDocumentClick = (event) => {
    // Get the TransitionRoot element (replace with the actual class or reference)
    const transitionRoot = document.querySelector(".transition-root"); // Replace with your selector
  
    if (!event.target.tagName === "path") {
      if (transitionRoot && !transitionRoot.contains(event.target)) {
        // Call the close function when a click occurs outside the TransitionRoot
        close();
      }
    } else {
      open.value = true;
    }
  };
  
  document.addEventListener("click", handleDocumentClick);
  </script>
  
  <script>
  export default {
    components: {
      VueCropper
    }
  }
  </script>
  
  <style scoped>
  /* Add any necessary styling for your component here */
  .image-container {
    max-width: 500px;
  }
  .cropped-image {
    text-align: center;
  }
  
  .cropped-image img {
    max-width: 100%;
    max-height: 400px; /* Adjust the maximum height as needed */
  }
  </style>
  